<template>
  <form
    v-if="isRevealed"
    class="space-y-4 p-4"
    @submit="onSubmit"
  >
    <FormInputText label="Label" name="label" />

    <div class="grid grid-cols-2 gap-4">
      <FormInputSelect
        label="Min Options"
        name="minOptions"
        :options="minOptionsOptions"
        disable-null
      />
      <FormInputSelect
        label="Max Options"
        name="maxOptions"
        :options="maxOptionsOptions"
        disable-null
      />
    </div>

    <div class="bg-background-fg/8 h-px w-full" />
    <section class="mb-2">
      <label
        class="text-background-fg/70 mb-1 block text-left text-sm font-medium"
      >
        Options
      </label>

      <VDraggable
        v-model="optionFieldsModel"
        item-key="key"
        handle=".handle"
        tag="ol"
        v-bind="dragOptions"
        class="mb-2 space-y-2"
      >
        <template #item="{ index }">
          <li
            class="bg-background flex items-center justify-around space-x-2 p-2 shadow"
          >
            <div>
              <button type="button" class="handle">
                <IconHeroiconsSolidBars3
                  class="h-5 w-5 text-gray-400"
                />
              </button>
            </div>
            <FormInputProductModifierOption
              label="Options"
              :name="`options[${index}]`"
            />
            <button
              class="flex items-center justify-center"
              @click="
                (event) => handleRemoveOption(event, index)
              "
            >
              <IconHeroiconsOutlineTrash />
            </button>
          </li>
        </template>
      </VDraggable>

      <div class="flex justify-center">
        <ButtonAdd @click="addOption">
          Add Option
        </ButtonAdd>
      </div>
    </section>

    <div class="grid grid-cols-2 gap-2">
      <ButtonSecondary type="button" @click="cancel">
        Back
      </ButtonSecondary>
      <FormSubmitButton>
        Save Modifier Group
      </FormSubmitButton>
    </div>
  </form>
</template>

<script setup lang="ts">
import VDraggable from "vuedraggable"
import { useFieldArray } from "vee-validate"
import type { ProductModifierGroupFormInput } from "@/composables/forms/useProductModifierGroupForm"

const $style = useCssModule()

const { reveal, isRevealed, confirm, cancel, onReveal } =
  useConfirmDialog<ProductModifierGroupFormInput, string>()

onReveal((params) => {
  resetForm()
  setValues(params)
})

defineExpose({ reveal, isRevealed })

const minOptionsOptions = Array.from(
  { length: 11 },
  (_, i) => ({
    value: i,
    label: String(i),
  })
)
const maxOptionsOptions = [
  ...minOptionsOptions.filter((i) => i.value > 0),
  { value: 99, label: "Unlimited" },
]

const {
  handleSubmit,
  setValues,
  values: formValues,
  resetForm,
} = useProductModifierGroupForm()

const onSubmit = handleSubmit(async (params) => {
  if ("id" in formValues) {
    const { id } = await updateProductModifier(
      formValues.id as string,
      params
    )
    confirm(id)
  } else {
    const { id } = await createProductModifier(params)
    confirm(id)
  }
})

const handleRemoveOption = (
  event: Event,
  index: number
) => {
  event.preventDefault()
  if (optionFields.value.length > 1) {
    removeOption(index)
  }
}

const {
  fields: optionFields,
  push: pushOption,
  remove: removeOption,
  replace,
} = useFieldArray("options")

const optionFieldsModel = computed({
  get: () => optionFields.value,
  set: (newFields) => {
    replace(newFields.map(({ value }) => value))
  },
})

const addOption = () => {
  pushOption({
    label: "",
    priceOffset: 0,
    isSelected: false,
  })
}
const dragOptions = computed(() => ({
  animation: 200,
  ghostClass: $style.ghost,
  dragClass: $style.drag,
  direction: "vertical",
}))
</script>

<style lang="scss" module>
.ghost {
  opacity: 0;
}

.drag {
  opacity: 1;
}
</style>
